import { useKeycloak } from 'src/stores/keycloak';
import { useMagicLink } from 'src/stores/magicLink';
import { isAuthDisabled, getAuthProvider } from 'src/auth/authConfig';
import { getShouldPauseTokenRefresh } from 'src/stores/useUserInactivityStore';
import http, { HttpResponse, PaginateConfig } from './http';
import { entraScopes } from 'src/auth/msalConfig';
import { captureSessionMessage } from 'src/utils/sentry/sessionSentry';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getQueryClient } from 'src/utils/queryClientRegistry';
import { handleSessionTermination } from 'src/utils/errors/sessionTerminationHandler';

// Import MSAL instance manager
import { getMsalInstance } from 'src/auth/msalInstanceManager';

/** Only refresh token when it expires within this window (ms). */
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const MAGIC_LINK_REFRESH_THRESHOLD_MS = 30 * 1000; // 30 seconds

/** Module-level cache for Entra token. Cleared when user continues session after inactivity. */
let entraTokenCache: { token: string; expiresAt: number } | null = null;

/**
 * Keycloak token refresh can be triggered concurrently by multiple requests
 * immediately after login. Serializing the refresh prevents backend session
 * invalidation races caused by multiple rapid token updates.
 */
let keycloakRefreshPromise: Promise<void> | null = null;

/**
 * Clear the Entra token cache. Call after user explicitly refreshes (e.g. Continue Session)
 * so the next request will acquire a fresh token from MSAL.
 */
export function clearEntraTokenCache(): void {
  captureSessionMessage('Clearing cached Entra token', 'info', {
    event: 'session.tokenCache.entra.cleared',
  });
  entraTokenCache = null;
}

/**
 * Handles paused token refresh state by returning cached token if available.
 */
function handlePausedEntraRefresh(): string | undefined {
  captureSessionMessage(
    'Token refresh paused while retrieving Entra access token',
    'warning',
    {
      event: 'session.tokenRefresh.paused.entra',
      extra: {
        usedCachedToken: !!entraTokenCache?.token,
      },
    },
  );
  return entraTokenCache?.token;
}

/**
 * Returns cached token if it is still valid and not close to expiration.
 */
function getValidCachedEntraToken(now: number): string | undefined {
  if (
    entraTokenCache?.token &&
    entraTokenCache.expiresAt > now + TOKEN_REFRESH_THRESHOLD_MS
  ) {
    return entraTokenCache.token;
  }
  return undefined;
}

/**
 * Handles errors during Entra token acquisition.
 */
function handleEntraTokenAcquisitionError(err: unknown): string | undefined {
  if (
    err instanceof Error &&
    err.message.includes('uninitialized_public_client_application')
  ) {
    return undefined;
  }

  if (err instanceof InteractionRequiredAuthError) {
    console.error('Entra token refresh requires user interaction:', err);
    const queryClient = getQueryClient();
    if (queryClient) {
      handleSessionTermination(queryClient).catch((terminationErr) => {
        console.error(
          'Error during session termination from token refresh:',
          terminationErr,
        );
      });
    }
    return undefined;
  }

  if (import.meta.env.DEV) {
    console.warn('Entra token acquisition error:', err);
  }

  return entraTokenCache?.token;
}

/**
 * Get Entra access token for API requests.
 * - When user is inactive: returns cached token only (no MSAL call).
 * - When user is active: uses cached token if still valid and not expiring soon;
 *   otherwise calls MSAL acquireTokenSilent and updates cache.
 * So we do not trigger token refresh on every request.
 */
export const getEntraAccessToken = async (): Promise<string | undefined> => {
  try {
    const msalInstance = getMsalInstance();
    const accounts = msalInstance.getAllAccounts();
    const account = msalInstance.getActiveAccount() || accounts[0];

    if (!account) {
      return undefined;
    }

    const now = Date.now();

    if (getShouldPauseTokenRefresh()) {
      return handlePausedEntraRefresh();
    }

    const cachedToken = getValidCachedEntraToken(now);
    if (cachedToken) {
      return cachedToken;
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: entraScopes(),
      account,
      forceRefresh: false,
    });

    const expiresAt = response.expiresOn
      ? response.expiresOn.getTime()
      : now + 60 * 60 * 1000;
    entraTokenCache = { token: response.accessToken, expiresAt };
    return response.accessToken;
  } catch (err) {
    return handleEntraTokenAcquisitionError(err);
  }
};

/**
 * Unified HTTP client with automatic authentication token injection
 *
 * This function automatically adds the appropriate Bearer token to HTTP requests
 * based on the configured authentication provider (Keycloak or Entra ID).
 * It maintains 100% backward compatibility with the existing keycloakTokenHttp function.
 *
 * Features:
 * - Automatic provider detection based on AUTH_PROVIDER environment variable
 * - Token refresh before requests to ensure validity
 * - Consistent error handling across both providers
 * - Backward compatibility with existing VITE_DISABLE_KEYCLOAK_AUTH flag
 *
 * @param url - The API endpoint URL
 * @param config - HTTP request configuration
 * @returns Promise<HttpResponse<T>> - HTTP response with typed data
 */
export const authTokenHttp = async <T>(
  url: string,
  config: PaginateConfig = {},
): Promise<HttpResponse<T>> => {
  // If authentication is disabled, use regular HTTP client
  if (isAuthDisabled()) {
    return http(url, config);
  }

  let token: string | undefined;

  try {
    const magicLinkState = useMagicLink.getState();
    const pauseRefresh = getShouldPauseTokenRefresh();

    if (magicLinkState.authenticated && magicLinkState.accessToken) {
      if (!pauseRefresh && magicLinkState.tokenExpiresAt) {
        if (
          magicLinkState.tokenExpiresAt.getTime() - Date.now() <
          MAGIC_LINK_REFRESH_THRESHOLD_MS
        ) {
          await magicLinkState.actions.updateToken();
        }
      }
      // Re-fetch in case it was updated
      token = useMagicLink.getState().accessToken;
    } else {
      const authProvider = getAuthProvider();
      if (authProvider === 'entra') {
        token = await getEntraAccessToken();
      } else if (authProvider === 'magic-link') {
        token = magicLinkState.actions.getAccessToken();
      } else {
        const keycloakState = useKeycloak.getState();
        if (pauseRefresh) {
          captureSessionMessage(
            'Token refresh paused while preparing authenticated HTTP request',
            'warning',
            {
              event: 'session.tokenRefresh.paused.httpRequest',
              authProvider: 'keycloak',
            },
          );
        } else {
          keycloakRefreshPromise ??= keycloakState.actions
            .updateToken()
            .then(() => undefined);
          try {
            await keycloakRefreshPromise;
          } finally {
            keycloakRefreshPromise = null;
          }
        }
        token = useKeycloak.getState().actions.getToken();
      }
    }
  } catch {
    // Continue without token - let the API handle the authentication error
  }

  // Make HTTP request with Authorization header if token is available
  return http(url, {
    ...config,
    headers: {
      ...config.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

/**
 * Legacy alias for backward compatibility
 * @deprecated Use authTokenHttp instead
 */
export const keycloakTokenHttp = authTokenHttp;

/**
 * Utility function to get the current authentication token
 * Works with Keycloak, Entra ID, and Magic Link providers
 * Priority: 1. Magic Link (if authenticated), 2. AUTH_PROVIDER config
 *
 * @returns Promise<string | undefined> - The current access token or undefined
 */
export const getAuthToken = async (): Promise<string | undefined> => {
  if (isAuthDisabled()) {
    return undefined;
  }

  const pauseRefresh = getShouldPauseTokenRefresh();
  const magicLinkState = useMagicLink.getState();

  if (magicLinkState.authenticated && magicLinkState.accessToken) {
    if (!pauseRefresh && magicLinkState.tokenExpiresAt) {
      if (
        magicLinkState.tokenExpiresAt.getTime() - Date.now() <
        MAGIC_LINK_REFRESH_THRESHOLD_MS
      ) {
        await magicLinkState.actions.updateToken();
      }
    }
    return useMagicLink.getState().accessToken;
  }

  try {
    const authProvider = getAuthProvider();
    if (authProvider === 'entra') {
      return await getEntraAccessToken();
    }
    if (authProvider === 'magic-link') {
      return magicLinkState.actions.getAccessToken();
    }
    const keycloakState = useKeycloak.getState();
    if (pauseRefresh) {
      captureSessionMessage(
        'Token refresh paused while getting auth token',
        'warning',
        {
          event: 'session.tokenRefresh.paused.getAuthToken',
          authProvider: 'keycloak',
        },
      );
    } else {
      keycloakRefreshPromise ??= keycloakState.actions
        .updateToken()
        .then(() => undefined);
      try {
        await keycloakRefreshPromise;
      } finally {
        keycloakRefreshPromise = null;
      }
    }
    return useKeycloak.getState().actions.getToken();
  } catch {
    return undefined;
  }
};

/**
 * Utility function to check if the user is authenticated
 * Works with Keycloak, Entra ID, and Magic Link providers
 * Priority: 1. Magic Link (if authenticated), 2. AUTH_PROVIDER config
 *
 * @returns boolean - True if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  if (isAuthDisabled()) {
    return true; // Consider authenticated when auth is disabled
  }

  // First, check if user is authenticated via magic link (regardless of AUTH_PROVIDER)
  const magicLinkState = useMagicLink.getState();
  if (magicLinkState.authenticated) {
    return true;
  }

  // If not authenticated via magic link, check the configured AUTH_PROVIDER
  const authProvider = getAuthProvider();

  if (authProvider === 'entra') {
    const msalInstance = getMsalInstance();
    const accounts = msalInstance.getAllAccounts();
    return !!(msalInstance.getActiveAccount() || accounts[0]);
  } else if (authProvider === 'magic-link') {
    return magicLinkState.authenticated;
  } else {
    const keycloakState = useKeycloak.getState();
    return keycloakState.authenticated;
  }
};

export default authTokenHttp;
